<template>
  <div class="modules-list-page">
    <h1 id="modules-page-title" class="visually-hidden">
      {{ $t("sidebar_items.modules") }}
    </h1>
    <div
      v-if="moduleTabs.length"
      class="modules-list-tabs d-flex align-items-stretch justify-content-start flex-wrap mb-3"
      role="tablist"
      aria-labelledby="modules-page-title"
    >
      <DashboardTabListItem
        v-for="tab in moduleTabs"
        :key="tab.value"
        :label="tab.label"
        :value="tab.value"
        :tab-element-id="'module-tab-' + tab.value"
        :is-active="activeTab === tab.value"
        :show-check-icon="isModuleActiveByValue[tab.value]"
        @on-tab-click="setActiveTab"
      />
    </div>
    <CContainer>
      <CRow class="d-flex align-items-center justify-content-center">
        <CCol
          lg="12"
          sm="12"
          class="v3-module-list pt-4 pb-4 rounded-5 bg-white min-w-0"
        >
          <div
            v-show="activeTab === 'appointment-pass'"
            role="tabpanel"
            id="module-panel-appointment-pass"
            aria-labelledby="module-tab-appointment-pass"
          >
            <appointment-pass-module-form v-if="isAppPassAval" />
          </div>
          <div
            v-show="activeTab === 'tardy-management'"
            role="tabpanel"
            id="module-panel-tardy-management"
            aria-labelledby="module-tab-tardy-management"
          >
            <tardy-management-module-form v-if="isTardyCheckInAval" />
          </div>
          <div
            v-show="activeTab === 'visitor-management'"
            role="tabpanel"
            id="module-panel-visitor-management"
            aria-labelledby="module-tab-visitor-management"
          >
            <VisitorManagementModuleForm v-if="isVisitorManagementAval" />
          </div>
          <div
            v-show="activeTab === 'behaviour-management'"
            role="tabpanel"
            id="module-panel-behaviour-management"
            aria-labelledby="module-tab-behaviour-management"
          >
            <BehaviourManagementModuleForm v-if="isBehaviourManagementAval" />
          </div>
          <div
            v-show="activeTab === 'queue-management'"
            role="tabpanel"
            id="module-panel-queue-management"
            aria-labelledby="module-tab-queue-management"
          >
            <QueueManagementModuleForm v-if="isQueueManagementAval" />
          </div>
          <div
            v-show="activeTab === 'auto-check-in'"
            role="tabpanel"
            id="module-panel-auto-check-in"
            aria-labelledby="module-tab-auto-check-in"
          >
            <auto-check-in-module-form v-if="isAutoCheckInAval" />
          </div>
          <div
            v-show="activeTab === 'kiosk'"
            role="tabpanel"
            id="module-panel-kiosk"
            aria-labelledby="module-tab-kiosk"
          >
            <kiosk-module-form v-if="isKioksAval" />
          </div>
          <div
            v-show="activeTab === 'digital-id-card'"
            role="tabpanel"
            id="module-panel-digital-id-card"
            aria-labelledby="module-tab-digital-id-card"
          >
            <digital-id-card-form v-if="isDigitalIdCardAval" />
          </div>
          <div
            v-show="activeTab === 'retain-extra-passes'"
            role="tabpanel"
            id="module-panel-retain-extra-passes"
            aria-labelledby="module-tab-retain-extra-passes"
          >
            <retain-extra-passes-module-form v-if="isRetainExtraPassesAval" />
          </div>
        </CCol>
      </CRow>
    </CContainer>
  </div>
</template>
<script>
import queueConstants from "@/constants/queueConstants"
import retainExtraPassesConstants from "@/constants/retainExtraPassesConstants"
import visitorConstants from "@/constants/visitorConstants.js"
import behaviourConstants from "@/constants/behaviourManagementConstants.js"
import AppointmentPassModuleForm from "@/v3components/Forms/ModuleListForms/AppointmentPassModuleForm.vue"
import AutoCheckInModuleForm from "@/v3components/Forms/ModuleListForms/AutoCheckInModuleForm.vue"
import DigitalIdCardForm from "@/v3components/Forms/ModuleListForms/DigitalIdCardForm.vue"
import KioskModuleForm from "@/v3components/Forms/ModuleListForms/KioskModuleForm.vue"
import QueueManagementModuleForm from "@/v3components/Forms/ModuleListForms/QueueManagementModuleForm.vue"
import RetainExtraPassesModuleForm from "@/v3components/Forms/ModuleListForms/RetainCanceledAppointmentsModuleForm.vue"
import TardyManagementModuleForm from "@/v3components/Forms/ModuleListForms/TardyManagementModuleForm.vue"
import VisitorManagementModuleForm from "@/v3components/Forms/ModuleListForms/VisitorManagementModuleForm.vue"
import DashboardTabListItem from "@/v3components/Datatables/DashboardDataTable/TabsAndFilter/DashboardTabListItem.vue"
import { computed, onMounted, ref, watch } from "vue"
import { useStore } from "vuex"
import BehaviourManagementModuleForm from "@/v3components/Forms/ModuleListForms/BehaviourManagementModuleForm.vue"

const MODULE_TAB_CONFIG = [
  { label: "Appointment Pass", value: "appointment-pass" },
  { label: "Tardy Management", value: "tardy-management" },
  { label: "Visitor Management", value: "visitor-management" },
  {
    label: "Behavior Management",
    value: "behaviour-management",
    terminologyKey: "behaviourManagement"
  },
  { label: "Queue Management", value: "queue-management" },
  { label: "Auto Check-In", value: "auto-check-in" },
  { label: "Kiosk", value: "kiosk" },
  { label: "Digital ID Card", value: "digital-id-card" },
  { label: "Retain Extra Passes", value: "retain-extra-passes" }
]

export default {
  name: "ModulesList",
  components: {
    DashboardTabListItem,
    KioskModuleForm,
    AutoCheckInModuleForm,
    AppointmentPassModuleForm,
    DigitalIdCardForm,
    TardyManagementModuleForm,
    VisitorManagementModuleForm,
    BehaviourManagementModuleForm,
    QueueManagementModuleForm,
    RetainExtraPassesModuleForm
  },
  setup() {
    const store = useStore()
    const activeTab = ref("appointment-pass")

    const availableSchoolModules = computed(
      () => store.getters["schools/availableSchoolModules"]
    )

    const isKioksAval = computed(() => {
      return (
        availableSchoolModules.value &&
        availableSchoolModules.value.includes("Kiosk")
      )
    })

    const isAppPassAval = computed(() => {
      return (
        availableSchoolModules.value &&
        availableSchoolModules.value.includes("Appointmentpass")
      )
    })

    const isAutoCheckInAval = computed(() => {
      return (
        availableSchoolModules.value &&
        availableSchoolModules.value.includes("Auto Check-In")
      )
    })

    const isTardyCheckInAval = computed(() => {
      return (
        availableSchoolModules.value &&
        availableSchoolModules.value.includes("Tardy Management")
      )
    })

    const isVisitorManagementAval = computed(() => {
      return (
        availableSchoolModules.value &&
        availableSchoolModules.value.includes(visitorConstants.MODULE) &&
        store.getters["visitorSettings/hasVisitorModuleAccess"]
      )
    })

    const isBehaviourManagementAval = computed(() => {
      const names = availableSchoolModules.value
      const hasBehaviourInSchool =
        Array.isArray(names) &&
        behaviourConstants.MODULE_ALIASES.some((alias) => names.includes(alias))
      return (
        hasBehaviourInSchool &&
        store.getters["behaviourSettings/hasBehaviourModuleAccess"]
      )
    })

    const isQueueManagementAval = computed(() => {
      return (
        availableSchoolModules.value &&
        availableSchoolModules.value.includes(queueConstants.MODULE) &&
        store.getters["queueSettings/hasQueueModuleAccess"]
      )
    })

    const isRetainExtraPassesAval = computed(() => {
      return (
        availableSchoolModules.value &&
        availableSchoolModules.value.includes(retainExtraPassesConstants.MODULE)
      )
    })

    const isDigitalIdCardAval = computed(() => {
      return (
        availableSchoolModules.value &&
        availableSchoolModules.value.includes("Digital ID Card")
      )
    })

    const activeModulesNames = computed(
      () => store.getters["schools/activeModulesNames"] || []
    )

    /** Matches DigitalIdCardForm: allow view on device when restrict_student_view_id is off */
    const digitalIdCardModuleOptions = computed(
      () => store.getters["schools/digitalIdCardModuleOptions"]
    )

    const isModuleActiveByValue = computed(() => ({
      "appointment-pass": activeModulesNames.value.includes("Appointmentpass"),
      "tardy-management": activeModulesNames.value.includes("Tardy Management"),
      "visitor-management": activeModulesNames.value.includes(
        visitorConstants.MODULE
      ),
      "behaviour-management": behaviourConstants.MODULE_ALIASES.some((alias) =>
        activeModulesNames.value.includes(alias)
      ),
      "queue-management": activeModulesNames.value.includes(
        queueConstants.MODULE
      ),
      "auto-check-in": activeModulesNames.value.includes("Auto Check-In"),
      kiosk: activeModulesNames.value.includes("Kiosk"),
      "digital-id-card":
        activeModulesNames.value.includes("Digital ID Card") &&
        !digitalIdCardModuleOptions.value?.restrict_student_view_id,
      "retain-extra-passes": activeModulesNames.value.includes(
        retainExtraPassesConstants.MODULE
      )
    }))

    const availabilityByValue = computed(() => ({
      "appointment-pass": isAppPassAval.value,
      "tardy-management": isTardyCheckInAval.value,
      "visitor-management": isVisitorManagementAval.value,
      "behaviour-management": isBehaviourManagementAval.value,
      "queue-management": isQueueManagementAval.value,
      "auto-check-in": isAutoCheckInAval.value,
      kiosk: isKioksAval.value,
      "digital-id-card": isDigitalIdCardAval.value,
      "retain-extra-passes": isRetainExtraPassesAval.value
    }))

    const moduleTabs = computed(() => {
      const term = store.getters["behaviourSettings/behaviourTerminology"]
      return MODULE_TAB_CONFIG.filter(
        (tab) => availabilityByValue.value[tab.value]
      ).map((tab) => ({
        ...tab,
        label:
          tab.terminologyKey && term?.[tab.terminologyKey]
            ? term[tab.terminologyKey]
            : tab.label
      }))
    })

    onMounted(() => {
      store.dispatch("behaviourSettings/fetchBehaviourTerminology")
    })

    watch(
      moduleTabs,
      (tabs) => {
        if (tabs.length && !tabs.some((t) => t.value === activeTab.value)) {
          activeTab.value = tabs[0].value
        }
      },
      { immediate: true }
    )

    const setActiveTab = (value) => {
      activeTab.value = value
    }

    return {
      activeTab,
      moduleTabs,
      setActiveTab,
      isModuleActiveByValue,
      isKioksAval,
      isAppPassAval,
      isAutoCheckInAval,
      isTardyCheckInAval,
      isVisitorManagementAval,
      isBehaviourManagementAval,
      isQueueManagementAval,
      isRetainExtraPassesAval,
      isDigitalIdCardAval
    }
  }
}
</script>
